import { useState } from 'react'
import type { Project } from '../data/projectTypes'
import { connectedIssueTracker, isAgentConnected, projectAgentPrompt } from '../lib/agentConnection'
import IssueTrackerChatCard from './chat/IssueTrackerChatCard'
import ModalPortal from './ModalPortal'
import './chat/chat.css'
import { AgentPrompt } from './AgentPrompt'
import ApiTokenControls from './ApiTokenControls'

export default function AgentConnectionSetup({ project, canEdit }: { project: Project; canEdit: boolean }) {
  const [trackerOpen, setTrackerOpen] = useState(false)
  const tracker = connectedIssueTracker(project)
  const projectUrl = `${window.location.origin}/projects/${project.id}/overview`
  return <div className="agent-connection testing-settings">
    <p>Connect your coding agent so it can review and fix the bugs QA reports for this project. Or connect an issue tracker and QA will send new bug reports there for your team to resolve.</p>
    <section aria-labelledby="agent-setup-title">
      <h2 id="agent-setup-title">Connect your coding agent</h2>
      <p>Open your app’s codebase in your coding agent and paste the prompt below. It includes your project link and the instructions for accessing QA.</p>
      <p>Project: <a href={projectUrl}>{project.name}</a></p>
      <AgentPrompt analyticsEvent="support-agent-prompt-copy" prompt={projectAgentPrompt(projectUrl)} />
      <h3>Give your agent an API token</h3>
      <p>Create a token below, copy it when it is shown, and provide it to your agent when asked. Tokens belong to your account and work across projects you can access. Ask your agent to read this project’s bug list to complete the connection, even if no bugs have been found yet.</p>
      <ApiTokenControls />
      <p role="status">{isAgentConnected(project) ? 'Agent connected' : 'Agent not connected'}</p>
      {!tracker && !isAgentConnected(project) && project.agent_oldest_unread_bug_at && <p>Ask your agent to read this project’s bug list. Unread bugs older than one hour need its attention.</p>}
      <p>Without a connected issue tracker, the connection turns red when bugs are more than one hour old and your agent has not read the bug list since they were reported.</p>
    </section>
    <section aria-labelledby="issue-tracker-title">
      <h2 id="issue-tracker-title">Issue trackers</h2>
      <p>{tracker ? `Connected to ${tracker.name}: ${tracker.destination}. QA sends new bug reports here.` : 'No issue tracker connected. Connect GitHub Issues, GitLab, Linear, Jira, or Asana.'}</p>
      <div className="testing-setup__actions">
        <button type="button" disabled={!canEdit} onClick={() => setTrackerOpen(true)}>
          {tracker ? 'Manage issue tracker' : 'Connect issue tracker'}
        </button>
      </div>
      {canEdit && trackerOpen && <ModalPortal>
        <dialog className="agent-connection__dialog" aria-label="Issue tracker setup"
          ref={node => { if (node && !node.open) node.showModal() }} onClose={() => setTrackerOpen(false)}>
          <IssueTrackerChatCard project={project} onClose={() => setTrackerOpen(false)} />
        </dialog>
      </ModalPortal>}
      {!canEdit && <p>A project member can connect an issue tracker.</p>}
    </section>
  </div>
}
